import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, jadeGreen, lightBlue, volcano } from '../../design-system/theme';
import { InfographicViewer } from './InfographicViewer';
import {
  CONSENT_FLOW_SYNTAX,
  WITHDRAWAL_FLOW_SYNTAX,
  GETTING_STARTED_SYNTAX,
  PLATFORM_FEATURES_SYNTAX,
  SECURITY_FEATURES_SYNTAX,
  COMPLIANCE_CHECKLIST_SYNTAX,
} from './infographicData';

// ===== STYLED COMPONENTS =====

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProcessCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid ${neutral[3]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const IconBadge = styled.div<{ $gradient: string }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${props => props.$gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
`;

const SectionTitle = styled.div`
  flex: 1;
  
  h2 {
    margin: 0 0 4px 0;
    font-size: 22px;
    font-weight: 700;
    color: ${theme.textPrimary};
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: ${theme.textSecondary};
    line-height: 1.5;
  }
`;

const ProcessSteps = styled.div`
  margin-top: 32px;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepItem = styled.div<{ $number: number; $color: string }>`
  display: flex;
  gap: 20px;
  padding: 24px;
  background: ${neutral[1]};
  border-radius: 12px;
  position: relative;
  transition: all 0.2s;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
  
  .step-number {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${props => props.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    flex-shrink: 0;
  }
  
  .step-content {
    flex: 1;
    
    h4 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: ${theme.textPrimary};
    }
    
    p {
      margin: 0 0 12px 0;
      font-size: 14px;
      color: ${theme.textSecondary};
      line-height: 1.6;
    }
    
    .tips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }
`;

const Tip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: white;
  border-radius: 6px;
  font-size: 12px;
  color: ${theme.textSecondary};
  
  .icon {
    font-size: 14px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid ${neutral[3]};
  padding-bottom: 16px;
  flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.$active ? `
    background: ${theme.primary};
    color: white;
    box-shadow: 0 4px 12px rgba(54, 58, 80, 0.3);
  ` : `
    background: ${neutral[2]};
    color: ${theme.textSecondary};
    
    &:hover {
      background: ${neutral[3]};
    }
  `}
`;

const AlertBox = styled.div<{ $type: 'info' | 'success' | 'warning' }>`
  padding: 20px 24px;
  border-radius: 12px;
  margin-top: 24px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  
  ${props => {
    switch (props.$type) {
      case 'info':
        return `background: ${lightBlue[1]}; border-left: 4px solid ${lightBlue[5]};`;
      case 'success':
        return `background: ${jadeGreen[1]}; border-left: 4px solid ${jadeGreen[5]};`;
      case 'warning':
        return `background: #fff8e6; border-left: 4px solid #f59e0b;`;
    }
  }}
  
  .icon {
    font-size: 24px;
    flex-shrink: 0;
  }
  
  .content {
    flex: 1;
    
    h4 {
      margin: 0 0 8px 0;
      font-size: 15px;
      font-weight: 600;
      color: ${theme.textPrimary};
    }
    
    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.6;
      color: ${theme.textPrimary};
    }
    
    ul {
      margin: 8px 0 0 0;
      padding-left: 20px;
      
      li {
        font-size: 14px;
        color: ${theme.textPrimary};
        line-height: 1.8;
      }
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 24px;
`;

const FeatureCard = styled.div`
  padding: 24px;
  background: ${neutral[1]};
  border-radius: 12px;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    
    .icon {
      font-size: 28px;
    }
    
    h4 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: ${theme.textPrimary};
    }
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: ${theme.textSecondary};
    line-height: 1.6;
  }
`;

const QuickActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 24px;
`;

const QuickAction = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border: 2px solid ${neutral[3]};
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    border-color: ${theme.primary};
    background: ${blueGrey[1]};
    transform: translateY(-2px);
  }
  
  .icon {
    font-size: 24px;
  }
  
  .label {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.textPrimary};
  }
`;

// ===== PROCESS DATA =====

type ProcessTab = 'consent-flow' | 'withdrawal' | 'data-erasure' | 'getting-started' | 'features';

const consentSteps = [
  {
    title: 'Create a Project',
    description: 'Define your data collection initiative. Specify the purpose, data categories, retention period, and coverage area.',
    tips: ['💡 Be specific about purpose', '📋 List all data categories'],
    color: blueGrey[7],
  },
  {
    title: 'Setup Templates',
    description: 'Create consent documents including privacy policy, terms of service, and consent text. Use the rich text editor or import from template library.',
    tips: ['📝 Use plain language', '🎨 Preview before publishing'],
    color: blueGrey[6],
  },
  {
    title: 'Configure Evidence',
    description: 'Choose how you want to capture proof of consent - digital signature, OTP verification, video/audio recording, or simple checkbox.',
    tips: ['🔒 Multi-evidence for high-value', '📱 OTP for identity verification'],
    color: volcano[6],
  },
  {
    title: 'Generate Form URL',
    description: 'Create a unique consent collection URL. Optionally set expiration time and customize redirect URLs for success/failure.',
    tips: ['⏰ Set appropriate expiration', '🔗 Use custom redirect URLs'],
    color: volcano[5],
  },
  {
    title: 'Send to Data Owner',
    description: 'Share the consent URL via email, SMS, or QR code. The system can send automatically or you can copy the link manually.',
    tips: ['📧 Email for formal consent', '📱 SMS for quick response'],
    color: jadeGreen[6],
  },
  {
    title: 'Collect & Store',
    description: 'Data owner completes the form. Evidence is captured, and consent artifact is securely stored with full audit trail.',
    tips: ['✅ Real-time validation', '🔐 Encrypted storage'],
    color: jadeGreen[5],
  },
];

const withdrawalSteps = [
  {
    title: 'Receive Request',
    description: 'Data owner submits withdrawal request through the My Consent Portal or contacts you directly. Request is logged in the system.',
    tips: ['📥 Track in withdrawals page', '🔔 Get notified instantly'],
    color: volcano[6],
  },
  {
    title: 'Verify Identity',
    description: 'Confirm the requestor is the actual data owner. Use available identification methods like email verification or phone confirmation.',
    tips: ['🔐 Security first', '📞 Call if needed'],
    color: volcano[5],
  },
  {
    title: 'Review Request',
    description: 'Examine the request details, associated consent artifacts, and any legal/contractual obligations that might affect processing.',
    tips: ['⚖️ Check legal obligations', '📊 Review impact'],
    color: blueGrey[7],
  },
  {
    title: 'Process Decision',
    description: 'Approve or reject the request. If rejecting, document the legal basis. Update consent status in the system.',
    tips: ['✅ Document reasoning', '📝 Keep records'],
    color: blueGrey[6],
  },
  {
    title: 'Notify Data Owner',
    description: 'Inform the data owner of your decision. Provide clear explanation if request was rejected with valid reasons.',
    tips: ['📧 Use clear language', '⏰ Respond within timeline'],
    color: jadeGreen[6],
  },
  {
    title: 'Trigger Data Erasure',
    description: 'After approval, system flags the consent artifact for data erasure. Navigate to the Data Erasure page to confirm deletion.',
    tips: ['🗑️ Check Data Erasure page', '📊 Complete erasure workflow'],
    color: jadeGreen[5],
  },
];

const dataErasureSteps = [
  {
    title: 'Review Pending Erasures',
    description: 'Navigate to the Data Erasure page to see all consents flagged for data deletion. These include withdrawn or expired consents.',
    tips: ['📋 Check daily', '🔔 Enable notifications'],
    color: volcano[6],
  },
  {
    title: 'Identify Data Locations',
    description: 'Review where the data owner\'s data exists - primary database, backups, third-party systems, analytics, logs.',
    tips: ['📍 Map all systems', '🔗 Check integrations'],
    color: volcano[5],
  },
  {
    title: 'Delete from All Systems',
    description: 'Remove the data from all identified locations including primary storage, backup systems, and notify third parties.',
    tips: ['🗑️ Include backups', '📤 Notify third parties'],
    color: blueGrey[7],
  },
  {
    title: 'Verify Deletion',
    description: 'Confirm data has been removed from all systems. Run verification checks and document the deletion process.',
    tips: ['✅ Run verification', '📝 Document steps'],
    color: blueGrey[6],
  },
  {
    title: 'Confirm Erasure',
    description: 'Click "Confirm Erasure" in the platform. Add notes about the deletion process. System records timestamp and confirmer.',
    tips: ['🖱️ Click Confirm Erasure', '📋 Add detailed notes'],
    color: jadeGreen[6],
  },
  {
    title: 'Audit Trail Created',
    description: 'System automatically logs the erasure confirmation for compliance. This record is stored with the consent artifact.',
    tips: ['📊 View in Audit Logs', '📄 Export for compliance'],
    color: jadeGreen[5],
  },
];

const platformFeatures = [
  { icon: '🏢', title: 'Multi-Tenant', description: 'Completely isolated data and configuration for each organization' },
  { icon: '📚', title: 'Template Library', description: 'Pre-built industry templates for quick deployment' },
  { icon: '📊', title: 'Analytics Dashboard', description: 'Track consent trends, conversion rates, and metrics' },
  { icon: '📝', title: 'Audit Logging', description: 'Complete activity tracking for compliance' },
  { icon: '🔌', title: 'API Integration', description: 'RESTful APIs for seamless automation' },
  { icon: '✅', title: 'Multi-Evidence', description: 'Signature, OTP, Video, and Audio capture' },
  { icon: '↩️', title: 'Withdrawal Management', description: 'Handle consent withdrawals efficiently' },
  { icon: '📥', title: 'Export & Reports', description: 'CSV/PDF exports for compliance reporting' },
  { icon: '🌐', title: 'Multi-Language', description: 'Support for multiple languages' },
  { icon: '📱', title: 'Mobile Responsive', description: 'Works perfectly on all devices' },
  { icon: '🔒', title: 'Enterprise Security', description: 'AES-256 encryption, MFA, and more' },
  { icon: '🎨', title: 'Custom Branding', description: 'White-label with your organization branding' },
];

// ===== COMPONENT =====

export const ProcessGuides: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ProcessTab>('consent-flow');

  const renderConsentFlow = () => (
    <>
      <InfographicViewer syntax={CONSENT_FLOW_SYNTAX} height={220} />
      
      <ProcessSteps>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: theme.textPrimary }}>
          Detailed Steps
        </h3>
        <StepList>
          {consentSteps.map((step, index) => (
            <StepItem key={index} $number={index + 1} $color={step.color}>
              <div className="step-number">{index + 1}</div>
              <div className="step-content">
                <h4>{step.title}</h4>
                <p>{step.description}</p>
                <div className="tips">
                  {step.tips.map((tip, i) => (
                    <Tip key={i}>{tip}</Tip>
                  ))}
                </div>
              </div>
            </StepItem>
          ))}
        </StepList>
      </ProcessSteps>

      <AlertBox $type="success">
        <span className="icon">✅</span>
        <div className="content">
          <h4>Best Practices for Consent Collection</h4>
          <ul>
            <li>Always use clear, plain language that anyone can understand</li>
            <li>Never use pre-checked boxes or implied consent</li>
            <li>Provide separate consent for different purposes</li>
            <li>Make it as easy to withdraw as it was to give consent</li>
            <li>Keep detailed records of when and how consent was given</li>
          </ul>
        </div>
      </AlertBox>
    </>
  );

  const renderWithdrawal = () => (
    <>
      <InfographicViewer syntax={WITHDRAWAL_FLOW_SYNTAX} height={280} />
      
      <ProcessSteps>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: theme.textPrimary }}>
          Withdrawal Process Steps
        </h3>
        <StepList>
          {withdrawalSteps.map((step, index) => (
            <StepItem key={index} $number={index + 1} $color={step.color}>
              <div className="step-number">{index + 1}</div>
              <div className="step-content">
                <h4>{step.title}</h4>
                <p>{step.description}</p>
                <div className="tips">
                  {step.tips.map((tip, i) => (
                    <Tip key={i}>{tip}</Tip>
                  ))}
                </div>
              </div>
            </StepItem>
          ))}
        </StepList>
      </ProcessSteps>

      <AlertBox $type="warning">
        <span className="icon">⚠️</span>
        <div className="content">
          <h4>Legal Considerations</h4>
          <p>
            Some withdrawal requests may need to be rejected if there's a legal obligation to continue 
            processing (e.g., contractual necessity, legal compliance). Always document your reasoning 
            and inform the data owner of their right to appeal or contact regulatory authorities.
          </p>
        </div>
      </AlertBox>

      <AlertBox $type="info">
        <span className="icon">⏰</span>
        <div className="content">
          <h4>Timeline Requirements</h4>
          <p>
            Under most privacy regulations, you must respond to withdrawal requests within specific timeframes:
          </p>
          <ul>
            <li><strong>DPDP Act:</strong> Process without unreasonable delay</li>
            <li><strong>GDPR:</strong> Within 1 month (extendable by 2 months for complex requests)</li>
            <li><strong>CCPA:</strong> Within 45 days (extendable by 45 days)</li>
          </ul>
        </div>
      </AlertBox>

      <AlertBox $type="success">
        <span className="icon">➡️</span>
        <div className="content">
          <h4>Next Step: Data Erasure</h4>
          <p>
            After approving a withdrawal, proceed to the <strong>Data Erasure</strong> tab to complete 
            the data deletion workflow and maintain compliance records.
          </p>
        </div>
      </AlertBox>
    </>
  );

  const renderDataErasure = () => (
    <>
      <ProcessSteps>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 600, color: theme.textPrimary }}>
          Data Erasure Confirmation Workflow
        </h3>
        <StepList>
          {dataErasureSteps.map((step, index) => (
            <StepItem key={index} $number={index + 1} $color={step.color}>
              <div className="step-number">{index + 1}</div>
              <div className="step-content">
                <h4>{step.title}</h4>
                <p>{step.description}</p>
                <div className="tips">
                  {step.tips.map((tip, i) => (
                    <Tip key={i}>{tip}</Tip>
                  ))}
                </div>
              </div>
            </StepItem>
          ))}
        </StepList>
      </ProcessSteps>

      <AlertBox $type="warning">
        <span className="icon">⚠️</span>
        <div className="content">
          <h4>Compliance Requirements</h4>
          <p>
            Data erasure confirmation is required by regulations like DPDP Act and GDPR. 
            The confirmation record is stored with the consent artifact as proof of compliance.
          </p>
          <ul>
            <li>Confirm erasure within regulatory timelines</li>
            <li>Document all deletion steps taken</li>
            <li>Notify third parties who received the data</li>
            <li>Maintain audit trail for compliance audits</li>
          </ul>
        </div>
      </AlertBox>

      <AlertBox $type="info">
        <span className="icon">📋</span>
        <div className="content">
          <h4>What Gets Recorded</h4>
          <p>
            When you confirm data erasure, the following information is stored with the consent artifact:
          </p>
          <ul>
            <li><strong>Erasure Timestamp:</strong> When the erasure was confirmed</li>
            <li><strong>Confirmed By:</strong> User who performed the confirmation</li>
            <li><strong>Notes:</strong> Details about the deletion process</li>
            <li><strong>Audit Log Entry:</strong> Automatic compliance record</li>
          </ul>
        </div>
      </AlertBox>

      <AlertBox $type="success">
        <span className="icon">✅</span>
        <div className="content">
          <h4>Best Practices</h4>
          <ul>
            <li>Process erasure requests promptly - don't let them pile up</li>
            <li>Verify deletion from ALL systems including backups</li>
            <li>Keep detailed notes about what was deleted and from where</li>
            <li>Set up regular reviews of pending erasure requests</li>
            <li>Export erasure records periodically for compliance documentation</li>
          </ul>
        </div>
      </AlertBox>
    </>
  );

  const renderGettingStarted = () => (
    <>
      <InfographicViewer syntax={GETTING_STARTED_SYNTAX} height={450} />
      
      <AlertBox $type="info">
        <span className="icon">🚀</span>
        <div className="content">
          <h4>Quick Start Checklist</h4>
          <ul>
            <li>✅ Create your organization account</li>
            <li>✅ Set up your organization profile with logo and contact details</li>
            <li>✅ Add at least one Data Fiduciary</li>
            <li>✅ Create your first project</li>
            <li>✅ Build or import consent templates</li>
            <li>✅ Generate a test consent form URL</li>
            <li>✅ Test the entire flow end-to-end</li>
            <li>✅ Invite team members and assign roles</li>
          </ul>
        </div>
      </AlertBox>

      <QuickActionGrid>
        <QuickAction href="#templates">
          <span className="icon">📝</span>
          <span className="label">Create Template</span>
        </QuickAction>
        <QuickAction href="#projects">
          <span className="icon">📁</span>
          <span className="label">New Project</span>
        </QuickAction>
        <QuickAction href="#generate-url">
          <span className="icon">🔗</span>
          <span className="label">Generate URL</span>
        </QuickAction>
        <QuickAction href="#analytics">
          <span className="icon">📊</span>
          <span className="label">View Analytics</span>
        </QuickAction>
      </QuickActionGrid>
    </>
  );

  const renderFeatures = () => (
    <>
      <InfographicViewer syntax={PLATFORM_FEATURES_SYNTAX} height={320} />
      
      <FeatureGrid>
        {platformFeatures.map((feature, index) => (
          <FeatureCard key={index}>
            <div className="header">
              <span className="icon">{feature.icon}</span>
              <h4>{feature.title}</h4>
            </div>
            <p>{feature.description}</p>
          </FeatureCard>
        ))}
      </FeatureGrid>

      <ProcessCard style={{ marginTop: '24px' }}>
        <SectionHeader>
          <IconBadge $gradient={`linear-gradient(135deg, ${jadeGreen[5]} 0%, ${jadeGreen[7]} 100%)`}>
            🔒
          </IconBadge>
          <SectionTitle>
            <h2>Security & Compliance</h2>
            <p>Enterprise-grade security for your consent data</p>
          </SectionTitle>
        </SectionHeader>
        <InfographicViewer syntax={SECURITY_FEATURES_SYNTAX} height={350} />
      </ProcessCard>

      <ProcessCard style={{ marginTop: '24px' }}>
        <SectionHeader>
          <IconBadge $gradient={`linear-gradient(135deg, ${lightBlue[5]} 0%, ${lightBlue[7]} 100%)`}>
            ✅
          </IconBadge>
          <SectionTitle>
            <h2>Compliance Checklist</h2>
            <p>Ensure your consent collection meets regulatory requirements</p>
          </SectionTitle>
        </SectionHeader>
        <InfographicViewer syntax={COMPLIANCE_CHECKLIST_SYNTAX} height={320} />
      </ProcessCard>
    </>
  );

  return (
    <Container>
      <ProcessCard>
        <TabsContainer>
          <Tab $active={activeTab === 'getting-started'} onClick={() => setActiveTab('getting-started')}>
            <span>🚀</span> Getting Started
          </Tab>
          <Tab $active={activeTab === 'features'} onClick={() => setActiveTab('features')}>
            <span>⭐</span> Platform Features
          </Tab>
          <Tab $active={activeTab === 'consent-flow'} onClick={() => setActiveTab('consent-flow')}>
            <span>📋</span> Consent Collection
          </Tab>
          <Tab $active={activeTab === 'withdrawal'} onClick={() => setActiveTab('withdrawal')}>
            <span>↩️</span> Withdrawal Process
          </Tab>
          <Tab $active={activeTab === 'data-erasure'} onClick={() => setActiveTab('data-erasure')}>
            <span>🗑️</span> Data Erasure
          </Tab>
        </TabsContainer>

        {activeTab === 'getting-started' && (
          <>
            <SectionHeader>
              <IconBadge $gradient={`linear-gradient(135deg, ${jadeGreen[5]} 0%, ${jadeGreen[7]} 100%)`}>
                🚀
              </IconBadge>
              <SectionTitle>
                <h2>Getting Started Guide</h2>
                <p>Your journey to compliant consent collection</p>
              </SectionTitle>
            </SectionHeader>
            {renderGettingStarted()}
          </>
        )}

        {activeTab === 'features' && (
          <>
            <SectionHeader>
              <IconBadge $gradient={`linear-gradient(135deg, ${lightBlue[5]} 0%, ${lightBlue[7]} 100%)`}>
                ⭐
              </IconBadge>
              <SectionTitle>
                <h2>Platform Features</h2>
                <p>Comprehensive capabilities for consent management</p>
              </SectionTitle>
            </SectionHeader>
            {renderFeatures()}
          </>
        )}

        {activeTab === 'consent-flow' && (
          <>
            <SectionHeader>
              <IconBadge $gradient={`linear-gradient(135deg, ${blueGrey[7]} 0%, ${blueGrey[9]} 100%)`}>
                📋
              </IconBadge>
              <SectionTitle>
                <h2>Consent Collection Process</h2>
                <p>Step-by-step guide to collecting digital consent from data owners</p>
              </SectionTitle>
            </SectionHeader>
            {renderConsentFlow()}
          </>
        )}

        {activeTab === 'withdrawal' && (
          <>
            <SectionHeader>
              <IconBadge $gradient={`linear-gradient(135deg, ${volcano[5]} 0%, ${volcano[7]} 100%)`}>
                ↩️
              </IconBadge>
              <SectionTitle>
                <h2>Consent Withdrawal Process</h2>
                <p>How to handle withdrawal requests from data owners</p>
              </SectionTitle>
            </SectionHeader>
            {renderWithdrawal()}
          </>
        )}

        {activeTab === 'data-erasure' && (
          <>
            <SectionHeader>
              <IconBadge $gradient={`linear-gradient(135deg, ${volcano[6]} 0%, ${volcano[8]} 100%)`}>
                🗑️
              </IconBadge>
              <SectionTitle>
                <h2>Data Erasure Workflow</h2>
                <p>Confirm data deletion for compliance after consent withdrawal or expiry</p>
              </SectionTitle>
            </SectionHeader>
            {renderDataErasure()}
          </>
        )}
      </ProcessCard>
    </Container>
  );
};

export default ProcessGuides;
